import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '~/lib/api';
import { NewKeyPanel } from '~/components/keys/NewKeyPanel';
import { RevokeAction } from '~/components/keys/RevokeAction';

interface Key {
  id: string;
  prefix: string;
  label: string;
  mode: 'live' | 'test';
  scopes: readonly string[];
  cidrs: readonly string[];
  status: 'active' | 'revoked';
  createdAt: string;
  revokedAt: string | null;
}

type Status =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ok'; data: Key[] };

export function KeysPage(): JSX.Element {
  const [status, setStatus] = useState<Status>({ kind: 'loading' });
  const [panelOpen, setPanelOpen] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    try {
      const res = await api<Key[]>('/dashboard/api-keys');
      setStatus({ kind: 'ok', data: res });
    } catch (err) {
      setStatus({
        kind: 'error',
        message: err instanceof ApiError ? err.message : 'Could not load keys',
      });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="mx-auto w-full max-w-[1280px] px-10 pt-6 pb-12">
      <div className="mb-5 flex items-end justify-between gap-6">
        <div>
          <h1 className="text-[22px] font-semibold -tracking-[0.015em]">
            Keys
          </h1>
          <p className="mt-1 max-w-[56ch] text-[14.5px] text-ink-secondary">
            Scoped credentials your service uses to call Seven. Mint one for
            each place that needs access.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setPanelOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-md bg-ink-primary px-3 py-2 text-[14px] font-semibold text-ink-on-dark hover:bg-ink-primary/90"
        >
          <svg
            width="11"
            height="11"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.75}
            strokeLinecap="round"
          >
            <path d="M8 3v10M3 8h10" />
          </svg>
          New key
        </button>
      </div>

      {status.kind === 'loading' && (
        <div className="rounded-lg border border-rule-hairline bg-surface-slip p-6 text-[14.5px] text-ink-secondary">
          Loading…
        </div>
      )}
      {status.kind === 'error' && (
        <div className="rounded-lg border border-stop/20 bg-stop-tint p-4 text-[14px] text-stop">
          {status.message}
        </div>
      )}
      {status.kind === 'ok' && status.data.length === 0 && (
        <div className="rounded-lg border border-rule-hairline bg-surface-slip p-10 text-center">
          <div className="text-[18px] font-semibold text-ink-primary">
            No keys yet
          </div>
          <p className="mt-1 text-[14.5px] text-ink-secondary">
            Mint one above to get started.
          </p>
        </div>
      )}

      {status.kind === 'ok' && status.data.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-rule-hairline bg-surface-slip">
          <div className="hidden grid-cols-[1.5fr_240px_180px_120px_80px] gap-5 border-b border-rule-hairline px-5 py-3 text-[11px] font-medium uppercase tracking-[0.08em] text-ink-watermark md:grid">
            <div>Key</div>
            <div>Scopes</div>
            <div>Allowed IPs</div>
            <div>Created</div>
            <div />
          </div>
          {status.data.map((k) => (
            <div
              key={k.id}
              className={`grid grid-cols-1 gap-3 border-b border-rule-hairline px-5 py-4 last:border-b-0 hover:bg-surface-margin/50 md:grid-cols-[1.5fr_240px_180px_120px_80px] md:gap-5 ${
                k.status === 'revoked' ? 'opacity-55' : ''
              }`}
            >
              <div>
                <div className="text-[14.5px] font-semibold text-ink-primary">
                  {k.label}
                </div>
                <div className="font-mono mt-0.5 text-[13px] text-ink-watermark">
                  {k.prefix}···
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-1">
                {k.scopes.map((s) => (
                  <ScopeChip key={s} scope={s} />
                ))}
              </div>
              <div className="flex items-center">
                {k.cidrs.length === 0 ? (
                  <span className="text-[14px] italic text-ink-watermark">
                    No restriction
                  </span>
                ) : (
                  <span className="font-mono text-[13.5px] text-ink-secondary">
                    {k.cidrs[0]}
                    {k.cidrs.length > 1 && (
                      <span className="ml-1 text-[11px] text-ink-watermark">
                        + {k.cidrs.length - 1}
                      </span>
                    )}
                  </span>
                )}
              </div>
              <div className="font-mono flex items-center text-[13.5px] text-ink-secondary">
                {formatDate(k.createdAt)}
              </div>
              <div className="flex items-center justify-end">
                <RevokeAction
                  keyId={k.id}
                  label={k.label}
                  prefix={k.prefix}
                  status={k.status}
                  onRevoked={() => void load()}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      <NewKeyPanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        onMinted={() => {
          void load();
        }}
      />
    </div>
  );
}

function ScopeChip({ scope }: { scope: string }): JSX.Element {
  const className =
    scope === '*'
      ? 'bg-ink-primary text-ink-on-dark'
      : scope === 'card:secrets'
        ? 'bg-notice-tint text-notice'
        : 'bg-surface-margin text-ink-secondary';
  return (
    <span
      className={`font-mono rounded px-1.5 py-[3px] text-[11px] font-medium ${className}`}
    >
      {scope}
    </span>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return iso;
  }
}
